import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../buttons/Button';
import { AnalyticsEventName } from '@/api/analytics';
import { AuthProviderOption } from '@/api/auth';
import { ImageComponent } from '@/components/Image';
import { getReactAppApiUrl } from '@/helpers/conf';
import { GOOGLE_OAUTH_NAME, IS_GOOGLE_AUTH_HIDDEN } from '@/helpers/oauth';
import { useAnalytics } from '@/hooks/analytics';
import useEstonia from '@/hooks/estonia';
import { useCommonStore } from '@/store';

import { ScContinueWithGoogle, ScDivider } from './styled';

export interface ContinueWithGoogleProps {
  dividerGap?: 'small' | 'large';
  marginBottom?: 'small' | 'large';
  analyticsEventName:
    | AnalyticsEventName.MODAL__LOGIN__GOOGLE_AUTH_BUTTON_CLICK
    | AnalyticsEventName.MODAL__WELCOME_BACK__GOOGLE_AUTH_BUTTON_CLICK
    | AnalyticsEventName.MODAL__REGISTRATION__GOOGLE_AUTH_BUTTON_CLICK;
}

export const ContinueWithGoogle = ({
  dividerGap = 'large',
  marginBottom = 'large',
  analyticsEventName,
}: ContinueWithGoogleProps) => {
  const { t } = useTranslation('common');
  const { sendEventToAnalytics } = useAnalytics();
  const { isEstonia } = useEstonia();

  const authProvidersOptions = useCommonStore.use.authProvidersOptions();
  const pendingAuthProvidersOptions =
    useCommonStore.use.pendingAuthProvidersOptions();

  const googleAuthOption = useMemo(
    () => authProvidersOptions.find(({ name }) => name === GOOGLE_OAUTH_NAME),
    [authProvidersOptions]
  );

  const handleRedirectToAuthProvider = (option: AuthProviderOption) => () => {
    const origin = getReactAppApiUrl(window.location.hostname);

    sendEventToAnalytics({ name: analyticsEventName });

    window.location.href = origin + option.url;
  };

  if (
    pendingAuthProvidersOptions ||
    !googleAuthOption ||
    isEstonia ||
    IS_GOOGLE_AUTH_HIDDEN
  )
    return null;

  return (
    <ScContinueWithGoogle dividerGap={dividerGap} marginBottom={marginBottom}>
      <Button
        wide
        blackTransparent
        onClick={handleRedirectToAuthProvider(googleAuthOption)}
      >
        <ImageComponent
          src="/common/googleIcon.webp"
          alt="google icon"
          width={24}
          height={24}
          className="image"
        />
        {t('modals.authorization.google')}
      </Button>
      <ScDivider>{t('modals.authorization.or')}</ScDivider>
    </ScContinueWithGoogle>
  );
};
